import React, { useState, useEffect, useRef } from "react"
import { Document, Page, pdfjs } from "react-pdf"
import {
  Printer,
  ArrowUp,
  ArrowRight,
  ArrowDown,
  ArrowLeft,
} from "lucide-react"

// ✅ Stable worker config (React 19 safe)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`

const ViewTab = ({ base64String, selectedFax, loading }) => {
  const [pdfUrl, setPdfUrl] = useState(null)
  const [numPages, setNumPages] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageRotations, setPageRotations] = useState({})
  const [pageWidth, setPageWidth] = useState(800)

  const containerRef = useRef(null)
  const canvasRef = useRef(null)

  /* -------------------------------
     Convert base64 → Blob URL
  -------------------------------- */
  useEffect(() => {
    if (!base64String) return

    try {
      const byteCharacters = atob(base64String)
      const byteNumbers = new Array(byteCharacters.length)
        .fill()
        .map((_, i) => byteCharacters.charCodeAt(i))
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: "application/pdf" })
      const url = URL.createObjectURL(blob)

      setPdfUrl(url)
      setCurrentPage(1)

      return () => URL.revokeObjectURL(url)
    } catch (err) {
      console.error("PDF load error:", err)
    }
  }, [base64String])

  /* -------------------------------
     Resize PDF to container
  -------------------------------- */
  useEffect(() => {
    if (!containerRef.current) return

    const updateWidth = () => {
      const containerWidth = containerRef.current.clientWidth
      setPageWidth(containerWidth)
    }

    updateWidth() // initial

    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(containerRef.current)

    return () => resizeObserver.disconnect()
  }, [])

  /* -------------------------------
     Handlers
  -------------------------------- */
  const handleLoadSuccess = ({ numPages }) => setNumPages(numPages)

  const handlePreviousPage = () => setCurrentPage((p) => Math.max(p - 1, 1))

  const handleNextPage = () => setCurrentPage((p) => Math.min(p + 1, numPages))

  const handleRotate = (page) => {
    setPageRotations((prev) => ({
      ...prev,
      [page]: ((prev[page] || 0) + 90) % 360,
    }))
  }

  // const getRotationIcon = () => {
  //   const r = pageRotations[currentPage] || 0
  //   return r ? `⟳ ${r}°` : "⟳"
  // }

  const handleDownload = () => {
    if (!pdfUrl) return

    const link = document.createElement("a")
    link.href = pdfUrl
    link.download = `${selectedFax?.fax_id || selectedFax?.faxId || "document"}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const getRotationIcon = () => {
    switch (pageRotations[currentPage] || 0) {
      case 0:
        return <ArrowUp size={20} color="#000" style={{ cursor: "pointer" }} /> // 0°
      case 90:
        return (
          <ArrowRight size={20} color="#000" style={{ cursor: "pointer" }} />
        ) // 90°
      case 180:
        return (
          <ArrowDown size={20} color="#000" style={{ cursor: "pointer" }} />
        ) // 180°
      case 270:
        return (
          <ArrowLeft size={20} color="#000" style={{ cursor: "pointer" }} />
        ) // 270°
      default:
        return <ArrowUp size={20} color="#000" style={{ cursor: "pointer" }} /> // Default to 0°
    }
  }

  const printPDF = (blob) => {
    const printWindow = window.open(pdfUrl)

    printWindow.onload = () => {
      printWindow.focus()
      printWindow.print()
    }
  }

  /* -------------------------------
     Render
  -------------------------------- */
  return (
    <div className="document-view">
      {loading ? (
        <div className="loading-container">
          <div className="loading-pdf-message">Loading ...</div>
        </div>
      ) : pdfUrl ? (
        <>
          {/* 🔀 Navigation (ONE ROW) */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <div
              className="navigation"
              style={{ marginLeft: "auto", marginRight: "auto" }}
            >
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                style={{ cursor: "pointer" }}
              >
                Previous
              </button>

              <span className="page-indicator">
                Page {currentPage} of {numPages}
              </span>

              <button
                onClick={handleNextPage}
                disabled={currentPage === numPages}
                style={{ cursor: "pointer" }}
              >
                Next
              </button>
            </div>

            {/* <div
              
              style={{
                paddingRight: "7px",
                cursor: "pointer",
                fontSize: "16px",
              }}
              title="Download PDF"
              onClick={() => handleDownload()}
            >
              📥
            </div> */}
            <div
              style={{
                paddingRight: "15px",
                cursor: "pointer",
                fontSize: "16px",
              }}
              title="Print"
              onClick={() => printPDF()}
            >
              <Printer size={20} color="#000" style={{ cursor: "pointer" }} />
            </div>
          </div>

          {/* 📄 PDF Viewer */}
          <div className="document-container" ref={containerRef}>
            <Document file={pdfUrl} onLoadSuccess={handleLoadSuccess}>
              <Page
                pageNumber={currentPage}
                rotate={pageRotations[currentPage] || 0}
                width={pageWidth}
                scale={1.05}
              />
            </Document>

            <canvas
              ref={canvasRef}
              className="document-canvas"
              // style={{ display: "none" }}
            />
          </div>

          {/* 🔁 Rotation Icon */}
          <div
            style={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <div
              className="rotation-icon"
              title="Rotate Page"
              onClick={() => handleRotate(currentPage)}
            >
              {getRotationIcon()}
            </div>
          </div>
        </>
      ) : (
        <p>
          {selectedFax?.efaxStatus === "INPROGRESS"
            ? "PDF not available as efax status is INPROGRESS"
            : "PDF not available"}
        </p>
      )}
    </div>
  )
}

export default ViewTab
